// Agent-side "Suggested knowledge" beside the ticket reply box. Auto-suggests
// published articles from the ticket subject, lets the agent search, preview,
// and insert an article's answer into their reply in one click — recording the
// share as a deflection signal.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { RichEditorContent } from '../RichEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { BookOpen, MagnifyingGlass, CaretDown, CaretRight, ArrowBendDownLeft, Eye } from '@phosphor-icons/react';

export default function KbSuggestPanel({ ticketId, seedQuery, category, onInsert }) {
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const norm = (s) => String(s || '').trim().toLowerCase();

  const search = useCallback(async (term) => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/kb/articles`, { ...h(), params: { status: 'published', q: term || '' } });
      // Queue/category-aware ranking (JSM-style): float articles whose category
      // matches this ticket's category to the top so agents see the most
      // relevant knowledge for the queue first. Falls back gracefully when
      // categories don't align.
      const cat = norm(category);
      const ranked = [...(data || [])].sort((a, b) => {
        const am = cat && norm(a.category) === cat ? 1 : 0;
        const bm = cat && norm(b.category) === cat ? 1 : 0;
        return bm - am;
      });
      setResults(ranked.slice(0, 6)); setUnavailable(false);
    } catch (e) { if (e?.response?.status === 402) setUnavailable(true); } finally { setLoading(false); }
  }, [API, h, currentOrg, category]);

  // Auto-suggest from the ticket subject the first time the panel opens.
  // Seed the search box so the debounced effect below runs it (and the agent
  // can see/edit the terms that produced the suggestions).
  useEffect(() => {
    if (!open) return;
    setQ((seedQuery || '').split(/\s+/).slice(0, 4).join(' '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  const insert = async (a) => {
    try {
      const { data } = await axios.get(`${API}/kb/articles/${a.article_id}`, h());
      const html = `<p><strong>${escapeHtml(data.title)}</strong></p>${data.body || ''}`;
      onInsert(html);
      axios.post(`${API}/kb/articles/${a.article_id}/shared`, { ticket_id: ticketId }, h()).catch(() => {});
      toast.success('Answer inserted from Knowledge Base');
    } catch (e) { toast.error('Could not insert article'); }
  };

  if (unavailable) return null;

  return (
    <div className="mt-2" data-testid="kb-suggest-panel">
      <button onClick={() => setOpen(o => !o)} data-testid="kb-suggest-toggle"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700">
        {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <BookOpen size={13} weight="fill" /> Suggested knowledge
        {results.length > 0 && !open && <span className="text-[10px] text-zinc-400">({results.length})</span>}
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
          <div className="relative mb-2">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search knowledge base…" data-testid="kb-suggest-search"
              className="w-full h-8 pl-8 pr-2 rounded-md border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          {loading ? (
            <p className="text-xs text-zinc-400 px-1 py-2">Searching…</p>
          ) : results.length === 0 ? (
            <p className="text-xs text-zinc-400 px-1 py-2">No published articles match. Try different words.</p>
          ) : (
            <div className="space-y-1.5">
              {results.map(a => (
                <div key={a.article_id} className="flex items-center gap-2 bg-white rounded-md ring-1 ring-zinc-200 px-3 py-2" data-testid={`kb-suggest-item-${a.article_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-zinc-800 truncate">{a.title}</p>
                      {category && norm(a.category) === norm(category) && (
                        <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100" data-testid={`kb-suggest-match-${a.article_id}`}>
                          In {a.category}
                        </span>
                      )}
                    </div>
                    {a.excerpt && <p className="text-[11px] text-zinc-400 truncate">{a.excerpt}</p>}
                  </div>
                  <button onClick={() => setPreview(a)} title="Preview" data-testid={`kb-suggest-preview-${a.article_id}`}
                    className="text-zinc-400 hover:text-zinc-700 p-1"><Eye size={15} /></button>
                  <button onClick={() => insert(a)} data-testid={`kb-suggest-insert-${a.article_id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-2 py-1 transition-colors">
                    <ArrowBendDownLeft size={12} /> Insert
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="kb-suggest-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.title}</DialogTitle></DialogHeader>
          {preview && <ArticleBody API={API} h={h} id={preview.article_id} onInsert={() => { insert(preview); setPreview(null); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ArticleBody({ API, h, id, onInsert }) {
  const [body, setBody] = useState(null);
  useEffect(() => { axios.get(`${API}/kb/articles/${id}`, h()).then(r => setBody(r.data.body || '<em>No content</em>')).catch(() => setBody('<em>Failed to load</em>')); }, [id]);
  return (
    <div>
      {body == null ? <p className="text-sm text-zinc-400">Loading…</p> : <div className="prose prose-sm max-w-none"><RichEditorContent html={body} /></div>}
      <div className="mt-4 flex justify-end">
        <button onClick={onInsert} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-3 py-1.5">
          <ArrowBendDownLeft size={14} /> Insert into reply
        </button>
      </div>
    </div>
  );
}

const escapeHtml = (s) => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
